/**
 * Pure helpers for the Platform Admin "Claude API gateway" card and the Claude
 * parts of the workflow model routing panel (CLAUDE.md §3.6). Kept free of
 * React Native imports so vitest can cover them.
 *
 * Backend: GET/PUT /api/admin/claude-gateway, PUT /api/admin/model-assignments/{key}
 * (backend/app/api/endpoints/platform_settings.py).
 */

export const CLAUDE_SLUG_PREFIX = 'anthropic-api/';

export interface ClaudeGatewayWorkloadSpend {
  workload_key: string;
  spent_usd: number;
  calls: number;
  cache_hit_rate: number;
}

export interface ClaudeGatewayStatus {
  month: string;
  spent_usd: number;
  monthly_cap_usd: number;
  percent_used: number;
  budget_exhausted: boolean;
  force_openrouter: boolean;
  api_key_configured: boolean;
  by_workload: ClaudeGatewayWorkloadSpend[];
}

export interface ClaudeGatewayUpdate {
  monthly_cap_usd?: number;
  force_openrouter?: boolean;
}

export type ReasoningEffort = 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max' | null;

export interface RoutedAssignment {
  workload_key: string;
  model: string;
  temperature: number | null;
  max_tokens: number;
  reasoning_effort: ReasoningEffort;
  fallback_model?: string | null;
  fallback_reasoning_effort?: ReasoningEffort;
  provider?: string;
  notes: string;
}

export const isClaudeSlug = (slug: string | null | undefined): boolean =>
  typeof slug === 'string' && slug.startsWith(CLAUDE_SLUG_PREFIX);

/** Which transport serves a workload, from the assignment's primary model. */
export const assignmentTransport = (assignment: Pick<RoutedAssignment, 'model'>): 'anthropic' | 'openrouter' =>
  isClaudeSlug(assignment.model) ? 'anthropic' : 'openrouter';

/**
 * One-click rollback: run the workload on its OpenRouter fallback model. The
 * fallback fields are sent unchanged so switching back to Claude later keeps them.
 */
export function rollbackToOpenRouterPayload(assignment: RoutedAssignment): RoutedAssignment | null {
  if (!isClaudeSlug(assignment.model) || !assignment.fallback_model || isClaudeSlug(assignment.fallback_model)) {
    return null;
  }
  return {
    workload_key: assignment.workload_key,
    model: assignment.fallback_model,
    temperature: assignment.temperature,
    max_tokens: assignment.max_tokens,
    reasoning_effort: assignment.fallback_reasoning_effort ?? assignment.reasoning_effort,
    fallback_model: assignment.fallback_model,
    fallback_reasoning_effort: assignment.fallback_reasoning_effort ?? null,
    notes: assignment.notes,
  };
}

/** The PUT body for a workload save: never sends the derived `provider` field. */
export function assignmentSavePayload(assignment: RoutedAssignment): RoutedAssignment {
  const payload: RoutedAssignment = { ...assignment };
  delete payload.provider;
  return payload;
}

export type CapParse = { ok: true; value: number } | { ok: false; errorKey: string };

/** Parse the monthly cap input (USD). 0 is allowed and means "no Claude spend". */
export function parseMonthlyCap(input: string): CapParse {
  const text = input.trim().replace(/^\$/, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    return { ok: false, errorKey: 'platformAdmin.claudeGateway.invalidCap' };
  }
  const value = Number.parseFloat(text);
  if (!Number.isFinite(value) || value > 100000) {
    return { ok: false, errorKey: 'platformAdmin.claudeGateway.invalidCap' };
  }
  return { ok: true, value };
}

export type BudgetTone = 'ok' | 'warning' | 'exhausted';

/** 80% matches the backend's first Telegram alert; 100% routes everything to OpenRouter. */
export function budgetTone(status: Pick<ClaudeGatewayStatus, 'percent_used' | 'budget_exhausted'>): BudgetTone {
  if (status.budget_exhausted || status.percent_used >= 100) return 'exhausted';
  if (status.percent_used >= 80) return 'warning';
  return 'ok';
}

/** The state line the card shows: why Claude is or is not serving traffic. */
export function gatewayStateKey(status: ClaudeGatewayStatus): string {
  if (!status.api_key_configured) return 'platformAdmin.claudeGateway.stateNoKey';
  if (status.force_openrouter) return 'platformAdmin.claudeGateway.stateForced';
  if (status.budget_exhausted) return 'platformAdmin.claudeGateway.stateExhausted';
  return 'platformAdmin.claudeGateway.stateActive';
}

export const formatUsd = (value: number): string => `$${(Number.isFinite(value) ? value : 0).toFixed(2)}`;

export const formatHitRate = (rate: number): string =>
  `${Math.round((Number.isFinite(rate) ? Math.max(0, Math.min(1, rate)) : 0) * 100)}%`;

export function claudeGatewayErrorKey(status: number | undefined): string {
  if (status === 403) return 'platformAdmin.claudeGateway.forbidden';
  if (status === 422 || status === 400) return 'platformAdmin.claudeGateway.invalidCap';
  return 'platformAdmin.claudeGateway.error';
}

export const CLAUDE_GATEWAY_I18N_KEYS = [
  'title',
  'description',
  'spent',
  'cap',
  'capPlaceholder',
  'saveCap',
  'forceOpenRouter',
  'resumeClaude',
  'stateActive',
  'stateNoKey',
  'stateForced',
  'stateExhausted',
  'byWorkload',
  'cacheHitRate',
  'calls',
  'noSpendYet',
  'saved',
  'invalidCap',
  'forbidden',
  'error',
] as const;
